import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { PoFormField } from '../../constants/constants';
import { AppRoute } from '../../config/route-config';

/**
 * Shape of each item passed from Dashboard's LocalSourcingComponent via
 * history.push state → matches `LocalSourcingItem` in Dashboard types.
 */
export interface LocalSourcingPrefillItem {
  amount?: string | null;
  quantity?: string | null;
  cashPrice?: string | null;
  totalCost?: string | null;
  // Backend field name for the RMS item number in the dashboard summary payload.
  itemNumber?: string | null;
  itemName?: string | null;
  rmsItemNumber?: string | null;
  weeklyRate?: string | null;
  weeklyTerm?: string | null;
  modelNumber?: string | null;
  fundingRequestItemId?: string | null;
  department?: string;
  subDepartment?: string;
  itemType?: string;
  subType?: string;
  bracket?: string;
  description?: string;
  brandName?: string;
  departmentId?: string;
  subDepartmentId?: string;
  bracketId?: string;
  itemTypeId?: string;
  subTypeId?: string;
}

/**
 * Shape of location.state set by Dashboard's LocalSourcingComponent
 * via `buildLocalSourcingRedirectState` → `redirectForAction('CREATE_MPO', obj)`.
 */
export interface LocalSourcingPrefillState {
  spendLimitId?: string;
  storeNumber?: string;
  vendorName?: string;
  vendorId?: string | null;
  supplierName?: string;
  supplierOrderNumber?: string;
  items?: LocalSourcingPrefillItem[];
}

const MAX_PRODUCTS = 4;

function hasLocalSourcingState(state: any): state is LocalSourcingPrefillState {
  return state && Array.isArray(state.items);
}

const getFieldName = (productIndex: number, field: PoFormField): string =>
  productIndex === 0 ? field : `products[${productIndex}].${field}`;

function setIfPresent(
  setValue: (name: string, value: any, options?: any) => void,
  name: string,
  value: string | null | undefined
) {
  if (value) {
    setValue(name, value, { shouldDirty: true });
  }
}

function applyItemFields(
  setValue: (name: string, value: any, options?: any) => void,
  item: LocalSourcingPrefillItem,
  idx: number,
  data: LocalSourcingPrefillState
) {
  setIfPresent(
    setValue,
    getFieldName(idx, PoFormField.Supplier),
    data.vendorName
  );
  setIfPresent(
    setValue,
    getFieldName(idx, PoFormField.SupplierOrderNo),
    data.supplierOrderNumber
  );
  setIfPresent(setValue, getFieldName(idx, PoFormField.Brand), item.brandName);
  setIfPresent(
    setValue,
    getFieldName(idx, PoFormField.Model),
    item.modelNumber
  );
  setIfPresent(
    setValue,
    getFieldName(idx, PoFormField.Description),
    item.description
  );
  setIfPresent(
    setValue,
    getFieldName(idx, PoFormField.Department),
    item.department
  );
  setIfPresent(
    setValue,
    getFieldName(idx, PoFormField.SubDepartment),
    item.subDepartment
  );
  setIfPresent(setValue, getFieldName(idx, PoFormField.Bracket), item.bracket);
  setIfPresent(
    setValue,
    getFieldName(idx, PoFormField.ItemType),
    item.itemType
  );
  setIfPresent(
    setValue,
    getFieldName(idx, PoFormField.ItemSubType),
    item.subType
  );
  setIfPresent(
    setValue,
    getFieldName(idx, PoFormField.RmsItemNumber),
    item.itemNumber || item.rmsItemNumber
  );

  if (item.department) {
    setValue(
      getFieldName(idx, PoFormField.StartClassificationsSelect),
      PoFormField.Department,
      { shouldDirty: true }
    );
  }

  setTimeout(() => {
    setIfPresent(
      setValue,
      getFieldName(idx, PoFormField.CostPerItem),
      item.amount
    );
    setIfPresent(setValue, `allocations[${idx}].quantity`, item.quantity);
  }, 0);
}

/**
 * Reads location.state synchronously and returns the initial product indexes
 * for LocalSourceRampDetails to use when initializing state.
 * Must be called during component render (not in an effect).
 */
export function useLocalSourcingPrefillCount(): number[] {
  const history = useHistory<LocalSourcingPrefillState | undefined>();
  const state = history.location.state;

  if (!hasLocalSourcingState(state)) return [0];

  const count = Math.min(state.items?.length || 1, MAX_PRODUCTS);
  if (count <= 1) return [0];

  return Array.from({ length: count }, (_, i) => i);
}

/**
 * Returns prefill PurchaseOrderFormValues-like objects for each product index.
 * Used by LocalSourceRampDetails to pass as the `purchaseOrder` prop so that
 * classification dropdowns display the matching name.
 */
export function useLocalSourcingPrefillPurchaseOrders(): Record<
  number,
  any
> | null {
  const history = useHistory<LocalSourcingPrefillState | undefined>();
  const state = history.location.state;

  if (!hasLocalSourcingState(state)) return null;

  const items = (state.items || []).slice(0, MAX_PRODUCTS);
  const result: Record<number, any> = {};

  items.forEach((item, idx) => {
    result[idx] = {
      department: item.department || '',
      subDepartment: item.subDepartment || '',
      bracket: item.bracket || '',
      itemType: item.itemType || '',
      itemSubType: item.subType || '',
      rmsItemNumber: item.itemNumber || item.rmsItemNumber || '',
      startClassificationsSelect: item.department ? PoFormField.Department : '',
      supplier: state.vendorName || '',
      brand: item.brandName || '',
      model: item.modelNumber || '',
      description: item.description || '',
      costPerItem: item.amount || '',
      itemCondition: 'New',
    };
  });

  return result;
}

/**
 * Reads pre-fill data from location.state (set by Dashboard
 * LocalSourcingComponent via history.push) and populates the LocalSource form
 * fields on mount. The state is consumed by replacing it without the prefill data.
 */
export function usePreFillFromDashboard() {
  const { setValue } = useFormContext();
  const history = useHistory<LocalSourcingPrefillState | undefined>();
  const locationState = history.location.state;

  useEffect(() => {
    if (!hasLocalSourcingState(locationState)) return;

    const data = locationState;

    // Consume prefill items so they don't re-apply, but keep spendLimitId
    // so PODetailsForm can have the local-source layout on it.
    history.replace({
      pathname: AppRoute.Create,
      search: history.location.search,
      state: { spendLimitId: data.spendLimitId },
    });

    const items = (data.items || []).slice(0, MAX_PRODUCTS);

    // Store spendLimitId for the submit PO request
    if (data.spendLimitId) {
      setValue('spendLimitId', data.spendLimitId, { shouldDirty: true });
    }

    // Preserve per-product RMS item numbers from the dashboard payload so the
    // pricing-summary fallback can send them regardless of classification state.
    setValue(
      'dashboardRmsItemNumbers',
      JSON.stringify(
        items.map((it) => it.itemNumber || it.rmsItemNumber || '')
      ),
      { shouldDirty: true }
    );

    // Pre-fill the primary store from dashboard (sets existing allocations[0] only)
    if (data.storeNumber) {
      setValue('allocations[0].storeNum', data.storeNumber, {
        shouldDirty: true,
      });
    }

    items.forEach((item, idx) => {
      applyItemFields(setValue, item, idx, data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export interface PrefillClassifications {
  department: string;
  subDepartment: string;
  bracket: string;
  itemType: string;
  itemSubType: string;
}
